/**
 * Build Your Own Bundle — page behavior.
 * No-build: edit this file directly; do not compile from src/.
 * Product card + cart qty are product-level (LIFO remove / latest-add).
 */

(function () {
  const MONEY_FORMAT = window.Shopify?.currency?.active || 'USD';

  function decodeHtml(str) {
    if (str == null || str === '') return str || '';
    const el = document.createElement('textarea');
    el.innerHTML = String(str);
    return el.value;
  }

  function decodeLabelMap(labels) {
    if (!labels || typeof labels !== 'object') return {};
    const out = {};
    Object.keys(labels).forEach((key) => {
      const value = labels[key];
      out[key] = typeof value === 'string' ? decodeHtml(value) : value;
    });
    return out;
  }

  function parseConfig(root) {
    const node = root.querySelector('[data-ctr-byob-config]');
    if (!node) return {};
    try {
      const config = JSON.parse(node.textContent);
      if (config.labels) config.labels = decodeLabelMap(config.labels);
      return config;
    } catch (error) {
      console.error('ctr-byob: invalid config JSON', error);
      return {};
    }
  }

  function parseProductJson(card) {
    const node = card.querySelector('[data-ctr-byob-product-json]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function excludedSellingPlanIdSet(config) {
    const ids = Array.isArray(config?.excludedSellingPlanIds) ? config.excludedSellingPlanIds : [];
    return new Set(ids.map((id) => String(id)));
  }

  function allowedSellingPlans(variant, excludedIds) {
    return (variant?.selling_plans || []).filter((plan) => !excludedIds.has(String(plan.id)));
  }

  function formatMoney(cents) {
    if (typeof window.Shopify?.formatMoney === 'function') {
      const format =
        document.querySelector('ctr-byob')?.dataset.moneyFormat ||
        window.theme?.moneyFormat ||
        '${{amount}}';
      return window.Shopify.formatMoney(cents, format);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function formatOptionSubtitle(source) {
    if (!source) return '';

    if (Array.isArray(source.options_with_values)) {
      const values = source.options_with_values
        .map((option) => option?.value)
        .filter((value) => value && value !== 'Default Title');
      if (values.length) return values.join(' / ');
    }

    if (Array.isArray(source.options)) {
      const values = source.options.filter((value) => value && value !== 'Default Title');
      if (values.length) return values.join(' / ');
    }

    const variantTitle = source.variant_title || source.variantTitle;
    if (typeof variantTitle === 'string' && variantTitle && variantTitle !== 'Default Title') {
      return variantTitle;
    }

    return '';
  }

  function escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  function isVariantPurchasable(variant) {
    if (!variant?.available) return false;
    if (variant.inventory_management !== 'shopify' || variant.inventory_policy === 'continue') {
      return true;
    }
    const minimum = Math.max(1, Number(variant.quantity_rule_min) || 1);
    return Number(variant.inventory_quantity) >= minimum;
  }

  function getHeaderOffset() {
    const headerSections = document.querySelectorAll('.shopify-section-group-header-group');
    let maxBottom = 0;
    headerSections.forEach((section) => {
      const rect = section.getBoundingClientRect();
      if (rect.bottom > maxBottom) maxBottom = rect.bottom;
    });
    if (maxBottom > 0) return Math.round(maxBottom);

    const siteHeader = document.querySelector('header.siteHeader, .header-wrapper, [data-site-header]');
    if (siteHeader) {
      return Math.round(siteHeader.getBoundingClientRect().bottom);
    }
    return 0;
  }

  function isHeaderVisible() {
    const header = document.querySelector(
      'header.siteHeader, .section-header, .header-wrapper, [data-site-header]'
    );
    if (!header) return false;
    if (header.classList.contains('shopify-section-header-hidden')) return false;
    return header.getBoundingClientRect().bottom > 0;
  }

  function getStickyTopOffset() {
    return isHeaderVisible() ? getHeaderOffset() : 0;
  }

  class CtrByob extends HTMLElement {
    constructor() {
      super();
      this.config = {};
      this.step = 'build';
      this.activeFilter = 'all';
      this.draft = new Map();
      this.addonsDraft = new Map();
      this.draftAddOrder = new Map();
      this.addonsAddOrder = new Map();
      this.frequencyOptions = [];
      this.selectedFrequency = '';
      this.loadingMore = false;
      this.buildCommitted = false;
      this.committedBundleGroupId = null;
      this.cartLines = [];
      this.bound = false;
      this.isAddingToCart = false;
      this.stickyTicking = false;
      this.paginationArmed = true;
      this.scrollPaginationBound = false;
    }

    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.config = parseConfig(this);
      this.excludedSellingPlanIds = excludedSellingPlanIdSet(this.config);
      this.minimumCents = Number(this.dataset.minimumCents) || this.config.minimumCents || 5000;
      this.firstDiscountPercent = Number(this.config.firstDiscountPercent);
      if (!Number.isFinite(this.firstDiscountPercent) || this.firstDiscountPercent < 0) {
        this.firstDiscountPercent = 5;
      }
      this.enableAddonsStep = this.config.enableAddonsStep === true;
      this.init();
    }

    init() {
      this.buildStep = this.querySelector('[data-ctr-byob-build-step]');
      this.addonsStep = this.querySelector('[data-ctr-byob-addons-step]');
      this.grid = this.querySelector('[data-ctr-byob-grid]');
      this.addonsGrid = this.querySelector('[data-ctr-byob-addons-grid]');
      this.tabsWrap = this.querySelector('[data-ctr-byob-tabs-wrap]');
      this.tabs = this.querySelector('[data-ctr-byob-tabs]');
      this.loader = this.querySelector('[data-ctr-byob-loader]');
      this.tabEmpty = this.querySelector('[data-ctr-byob-tab-empty]');

      this.collectFrequencyOptions();
      this.populateFrequencySelects();
      this.ensureFrequencySelection();
      this.bindTabs();
      this.bindCards(this);
      this.bindPanel();
      this.bindInfiniteScroll();
      this.updatePaginationState();
      this.applyTabFilter();
      this.updateUI();
      this.bindStickyOffsetSync();
      this.refreshYotpoWidgets();
    }

    refreshYotpoWidgets(attempt = 0) {
      if (
        typeof yotpoWidgetsContainer !== 'undefined' &&
        typeof yotpoWidgetsContainer.initWidgets === 'function'
      ) {
        yotpoWidgetsContainer.initWidgets();
        return;
      }

      if (attempt >= 20) return;
      window.setTimeout(() => this.refreshYotpoWidgets(attempt + 1), 300);
    }

    ensureFrequencySelection() {
      if (!this.selectedFrequency && this.frequencyOptions.length) {
        this.selectedFrequency = this.frequencyOptions[0];
      }

      this.querySelectorAll(
        '[data-ctr-byob-frequency-select], [data-ctr-byob-mobile-frequency-select]'
      ).forEach((select) => {
        if (this.selectedFrequency) {
          select.value = this.selectedFrequency;
        }
      });
    }

    bindStickyOffsetSync() {
      const sync = () => this.syncStickyTabsOffset();

      const onScroll = () => {
        if (this.stickyTicking) return;
        this.stickyTicking = true;
        requestAnimationFrame(() => {
          sync();
          this.stickyTicking = false;
        });
      };

      sync();
      window.addEventListener('resize', sync, { passive: true });
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('load', sync, { passive: true });

      if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(sync);
        const headerSections = document.querySelectorAll('.shopify-section-group-header-group');
        headerSections.forEach((section) => observer.observe(section));

        const previewBar = document.getElementById('PBarNextFrameWrapper');
        if (previewBar) observer.observe(previewBar);
      }
    }

    collectFrequencyOptions() {
      const names = new Set();
      this.querySelectorAll('[data-ctr-byob-card]').forEach((card) => {
        if (card.dataset.requiresSubscription === 'false') return;
        const product = parseProductJson(card);
        product?.variants?.forEach((variant) => {
          allowedSellingPlans(variant, this.excludedSellingPlanIds).forEach((plan) => {
            if (plan.name) names.add(plan.name);
          });
        });
      });
      this.frequencyOptions = Array.from(names);
      if (this.selectedFrequency && !this.frequencyOptions.includes(this.selectedFrequency)) {
        this.selectedFrequency = '';
      }
      if (!this.selectedFrequency && this.frequencyOptions.length) {
        this.selectedFrequency = this.frequencyOptions[0];
      }
    }

    populateFrequencySelects() {
      const selects = this.querySelectorAll(
        '[data-ctr-byob-frequency-select], [data-ctr-byob-mobile-frequency-select]'
      );
      selects.forEach((select) => {
        select.innerHTML = '';
        this.frequencyOptions.forEach((name) => {
          const option = document.createElement('option');
          option.value = name;
          option.textContent = name;
          if (name === this.selectedFrequency) option.selected = true;
          select.appendChild(option);
        });

        if (this.selectedFrequency) {
          select.value = this.selectedFrequency;
        }

        if (select.dataset.bound === 'true') return;
        select.dataset.bound = 'true';
        select.addEventListener('change', (event) => {
          this.selectedFrequency = event.target.value;
          this.querySelectorAll(
            '[data-ctr-byob-frequency-select], [data-ctr-byob-mobile-frequency-select]'
          ).forEach((el) => {
            if (el !== event.target) el.value = this.selectedFrequency;
          });
          this.ensureFrequencySelection();
        });
      });
      this.ensureFrequencySelection();
    }

    resolveSellingPlanId(product, variantId) {
      if (!product?.variants?.length || !this.selectedFrequency) return null;
      const variant = product.variants.find((item) => String(item.id) === String(variantId));
      if (!variant) return null;
      const plans = allowedSellingPlans(variant, this.excludedSellingPlanIds);
      const match = plans.find((plan) => plan.name === this.selectedFrequency);
      return match?.id || plans[0]?.id || null;
    }

    bindTabs() {
      if (!this.tabs) return;
      const tabButtons = this.tabs.querySelectorAll('[data-ctr-byob-tab]');
      tabButtons.forEach((tab) => {
        tab.addEventListener('click', () => {
          this.activeFilter = tab.dataset.filterKey || 'all';
          tabButtons.forEach((btn) => {
            const active = btn === tab;
            btn.classList.toggle('is-active', active);
            btn.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          this.resetPaginationOnTabChange();
          this.applyTabFilter();
        });
      });

      const initialTab = this.tabs.querySelector('[data-ctr-byob-tab].is-active') || tabButtons[0];
      if (initialTab) {
        this.activeFilter = initialTab.dataset.filterKey || 'all';
      }
    }

    applyTabFilter() {
      if (!this.grid) return;
      const activeTab = this.config.tabs?.find((tab) => tab.filter_key === this.activeFilter);
      let visibleCount = 0;

      this.grid.querySelectorAll('[data-ctr-byob-card]').forEach((card) => {
        const keys = (card.dataset.filterKeys || 'all')
          .split(',')
          .map((key) => key.trim())
          .filter(Boolean);
        const handles = (card.dataset.collectionHandles || '')
          .split(',')
          .map((handle) => handle.trim())
          .filter(Boolean);

        let show = false;
        if (this.activeFilter === 'all') {
          show = true;
        } else if (activeTab?.collection_handle) {
          show = handles.includes(activeTab.collection_handle);
          if (show && (this.activeFilter === 'ground' || this.activeFilter === 'burgers')) {
            show = keys.includes(this.activeFilter);
          }
        } else {
          show = keys.includes(this.activeFilter);
        }

        card.classList.toggle('ctr-byob-card--filtered-out', !show);
        card.hidden = !show;
        if (show) visibleCount += 1;
      });

      this.sortGridForActiveTab();

      if (this.tabEmpty) {
        const showEmpty = this.activeFilter !== 'all' && visibleCount === 0 && !this.grid.dataset.nextUrl;
        this.tabEmpty.hidden = !showEmpty;
      }

      this.updatePaginationState();
      this.maybeAutoLoadForActiveTab(visibleCount);
    }

    sortGridForActiveTab() {
      if (!this.grid) return;
      const cards = Array.from(this.grid.querySelectorAll('[data-ctr-byob-card]'));
      if (!cards.some((card) => card.dataset.tabOrders)) return;

      const active = this.activeFilter || 'all';
      const orderOf = (card) => {
        if (!card.dataset.tabOrders) return Number.MAX_SAFE_INTEGER;
        try {
          const orders = JSON.parse(card.dataset.tabOrders);
          const value = orders[active];
          if (value == null || value === '') return Number.MAX_SAFE_INTEGER;
          const num = Number(value);
          return Number.isFinite(num) ? num : Number.MAX_SAFE_INTEGER;
        } catch (error) {
          return Number.MAX_SAFE_INTEGER;
        }
      };

      cards.sort((a, b) => orderOf(a) - orderOf(b));
      cards.forEach((card) => this.grid.appendChild(card));
    }

    getVisibleCardCount() {
      if (!this.grid) return 0;
      return Array.from(this.grid.querySelectorAll('[data-ctr-byob-card]')).filter(
        (card) => !card.hidden
      ).length;
    }

    resetPaginationOnTabChange() {
      if (this.loader) {
        this.loader.classList.remove('is-loading');
      }
      this.paginationArmed = true;
      this.updatePaginationState();
    }

    shouldTriggerIntersectionLoad() {
      if (!this.paginationArmed) return false;
      if (!this.grid?.dataset.nextUrl || this.loadingMore) return false;
      if (this.step !== 'build') return false;
      if (!this.isNearGridBottom()) return false;
      if (this.activeFilter === 'all') return true;
      return this.getVisibleCardCount() > 0;
    }

    isNearGridBottom(threshold = 480) {
      if (!this.grid) return false;
      const rect = this.grid.getBoundingClientRect();
      return rect.bottom <= window.innerHeight + threshold;
    }

    armPaginationFromScroll() {
      if (!this.isNearGridBottom()) {
        this.paginationArmed = true;
      }
    }

    maybeAutoLoadForActiveTab(visibleCount) {
      if (this.loadingMore) return;
      if (this.activeFilter === 'all') return;
      if (visibleCount > 0) return;
      if (!this.grid?.dataset.nextUrl) return;
      this.loadMoreProducts();
    }

    updatePaginationState() {
      if (!this.loader || !this.grid) return;
      const hasNext = Boolean(this.grid.dataset.nextUrl);
      this.loader.hidden = !hasNext;
      if (!this.loadingMore) {
        this.loader.classList.remove('is-loading');
      }

      if (!this.paginationObserver) return;
      this.paginationObserver.disconnect();
      if (hasNext && !this.loadingMore) {
        this.paginationObserver.observe(this.loader);
      }
    }

    appendLoadedCards(cards) {
      if (!this.grid || !cards.length) return;
      const fragment = document.createDocumentFragment();
      cards.forEach((card) => fragment.appendChild(card));
      this.grid.appendChild(fragment);
    }

    bindCards(scope) {
      scope.querySelectorAll('[data-ctr-byob-card]').forEach((card) => {
        if (card.dataset.bound === 'true') return;
        card.dataset.bound = 'true';

        const product = parseProductJson(card);
        const isSimplified = card.dataset.cardLayout === 'simplified';
        const gradeSelect = card.querySelector('[data-ctr-byob-grade-select]');
        const variantSelect = card.querySelector('[data-ctr-byob-variant-select]');
        const sizeButtons = card.querySelectorAll('[data-ctr-byob-size-option]');
        const minus = card.querySelector('[data-ctr-byob-qty-minus]');
        const plus = card.querySelector('[data-ctr-byob-qty-plus]');
        const addBtn = card.querySelector('[data-ctr-byob-add]');

        const applyVariant = (variant) => {
          if (!variant) return;
          const requiresSubscription = card.dataset.requiresSubscription !== 'false';
          const hasSellingPlan =
            !requiresSubscription ||
            Boolean(allowedSellingPlans(variant, this.excludedSellingPlanIds).length);
          const isAvailable = isVariantPurchasable(variant);
          const canAddVariant = isAvailable && hasSellingPlan;
          card.dataset.variantId = variant.id;
          card.dataset.priceCents = variant.price;
          card.dataset.available = canAddVariant ? 'true' : 'false';
          const priceEl = card.querySelector('[data-ctr-byob-card-price]');
          if (priceEl) {
            const current = priceEl.querySelector('.ctr-byob-card__price-current');
            const compare = priceEl.querySelector('.ctr-byob-card__price-compare');
            if (current) current.textContent = formatMoney(variant.price);
            if (compare) {
              if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                compare.textContent = formatMoney(variant.compare_at_price);
                compare.hidden = false;
              } else {
                compare.hidden = true;
              }
            }
          }
          const image = card.querySelector('[data-ctr-byob-card-image]');
          if (image && variant.featured_image) {
            image.src = variant.featured_image;
          }
          card.classList.toggle('ctr-byob-card--sold-out', !isAvailable);
          card.classList.toggle('ctr-byob-card--no-subscription', !hasSellingPlan);
          if (addBtn) {
            addBtn.disabled = !canAddVariant;
            addBtn.textContent = isAvailable
              ? addBtn.dataset.addLabel || 'ADD'
              : addBtn.dataset.outOfStockLabel || 'OUT OF STOCK';
            addBtn.setAttribute(
              'aria-label',
              isAvailable
                ? addBtn.dataset.addAria || addBtn.dataset.addLabel || 'ADD'
                : addBtn.dataset.outOfStockAria || addBtn.dataset.outOfStockLabel || 'OUT OF STOCK'
            );
          }
          if (plus) plus.disabled = !canAddVariant;
        };

        const updateVariant = () => {
          if (!product) return;

          if (isSimplified && variantSelect) {
            const selectedId = String(variantSelect.value || '');
            const variant =
              product.variants.find((v) => String(v.id) === selectedId) || product.variants[0];
            applyVariant(variant);
            return;
          }

          const grade = gradeSelect?.value;
          const sizeBtn = card.querySelector('[data-ctr-byob-size-option].is-selected');
          const size = sizeBtn?.dataset.sizeValue;
          let variant = product.variants[0];
          if (grade || size) {
            variant =
              product.variants.find((v) => {
                const gradeMatch = !grade || v.options.includes(grade);
                const sizeMatch = !size || v.options.includes(size);
                return gradeMatch && sizeMatch;
              }) || variant;
          }
          applyVariant(variant);
        };

        gradeSelect?.addEventListener('change', () => {
          updateVariant();
          this.syncProductCardQty(product?.id, card.dataset.addons === 'true');
        });
        variantSelect?.addEventListener('change', () => {
          updateVariant();
          this.syncProductCardQty(product?.id, card.dataset.addons === 'true');
        });
        sizeButtons.forEach((btn) => {
          btn.addEventListener('click', () => {
            sizeButtons.forEach((b) => {
              b.classList.remove('is-selected');
              b.setAttribute('aria-pressed', 'false');
            });
            btn.classList.add('is-selected');
            btn.setAttribute('aria-pressed', 'true');
            updateVariant();
            this.syncProductCardQty(product?.id, card.dataset.addons === 'true');
          });
        });

        minus?.addEventListener('click', () => this.adjustProductQtyFromCard(card, -1));
        plus?.addEventListener('click', () => this.adjustProductQtyFromCard(card, 1));
        addBtn?.addEventListener('click', () => this.adjustProductQtyFromCard(card, 1));
        updateVariant();
        this.syncProductCardQty(product?.id, card.dataset.addons === 'true');
      });
    }

    getAddOrderMap(isAddons) {
      return isAddons ? this.addonsAddOrder : this.draftAddOrder;
    }

    getDraftMap(isAddons) {
      return isAddons ? this.addonsDraft : this.draft;
    }

    getProductQty(productId, isAddons = false) {
      if (productId == null) return 0;
      const id = String(productId);
      let total = 0;
      this.getDraftMap(isAddons).forEach((line) => {
        if (String(line.productId) === id) total += line.qty;
      });
      return total;
    }

    syncProductCardQty(productId, isAddons = false) {
      if (productId == null) return;
      const total = this.getProductQty(productId, isAddons);
      const selector = isAddons
        ? `[data-ctr-byob-card][data-addons="true"][data-product-id="${productId}"]`
        : `[data-ctr-byob-card][data-addons="false"][data-product-id="${productId}"], [data-ctr-byob-card]:not([data-addons="true"])[data-product-id="${productId}"]`;

      this.querySelectorAll(selector).forEach((card) => {
        if (isAddons && card.dataset.addons !== 'true') return;
        if (!isAddons && card.dataset.addons === 'true') return;
        const valueEl = card.querySelector('[data-ctr-byob-qty-value]');
        if (valueEl) valueEl.textContent = String(total);
        const isAdded = total > 0;
        card.classList.toggle('ctr-byob-card--added', isAdded);
        const stepper = card.querySelector('[data-ctr-byob-card-stepper]');
        if (stepper && card.dataset.cardLayout === 'simplified') {
          stepper.hidden = !isAdded;
        }
      });
    }

    ensureDraftLine(map, { variantId, productData, priceCents, variant }) {
      const key = String(variantId);
      const existing = map.get(key) || {
        variantId: Number(variantId),
        productId: productData?.id,
        title: productData?.title,
        image: productData?.image,
        priceCents: Number(priceCents) || 0,
        compareCents: 0,
        optionSubtitle: '',
        qty: 0,
        product: productData,
      };

      existing.priceCents = Number(priceCents) || existing.priceCents || 0;
      if (variant?.featured_image) {
        existing.image = variant.featured_image;
      }
      existing.optionSubtitle = formatOptionSubtitle(variant);
      if (variant?.compare_at_price && variant.compare_at_price > variant.price) {
        existing.compareCents = variant.compare_at_price;
      } else {
        existing.compareCents = 0;
      }

      return existing;
    }

    pushAddOrder(productId, variantId, isAddons) {
      if (productId == null || variantId == null) return;
      const orderMap = this.getAddOrderMap(isAddons);
      const key = String(productId);
      const stack = orderMap.get(key) || [];
      stack.push(String(variantId));
      orderMap.set(key, stack);
    }

    popAddOrder(productId, isAddons) {
      if (productId == null) return null;
      const orderMap = this.getAddOrderMap(isAddons);
      const key = String(productId);
      const stack = orderMap.get(key) || [];
      while (stack.length) {
        const variantId = stack.pop();
        const line = this.getDraftMap(isAddons).get(String(variantId));
        if (line?.qty > 0) {
          orderMap.set(key, stack);
          return String(variantId);
        }
      }
      orderMap.set(key, []);
      return null;
    }

    removeAddOrderEntry(productId, variantId, isAddons) {
      if (productId == null || variantId == null) return;
      const orderMap = this.getAddOrderMap(isAddons);
      const key = String(productId);
      const stack = orderMap.get(key) || [];
      for (let i = stack.length - 1; i >= 0; i -= 1) {
        if (String(stack[i]) === String(variantId)) {
          stack.splice(i, 1);
          break;
        }
      }
      orderMap.set(key, stack);
    }

    findFallbackVariantToRemove(productId, isAddons) {
      const id = String(productId);
      let fallback = null;
      this.getDraftMap(isAddons).forEach((line, variantId) => {
        if (String(line.productId) !== id || line.qty <= 0) return;
        fallback = String(variantId);
      });
      return fallback;
    }

    adjustProductQtyFromCard(card, delta) {
      const productData = parseProductJson(card);
      const productId = productData?.id || card.dataset.productId;
      if (productId == null) return;

      const isAddons = card.dataset.addons === 'true';
      const map = this.getDraftMap(isAddons);
      let variantId = null;

      if (delta > 0) {
        // + adds the currently selected options (becomes the latest added unit).
        variantId = String(card.dataset.variantId || '');
        if (!variantId) return;
        if (card.dataset.available === 'false') return;

        const variant = productData?.variants?.find((v) => String(v.id) === variantId);
        const line = this.ensureDraftLine(map, {
          variantId,
          productData,
          priceCents: card.dataset.priceCents,
          variant,
        });
        line.qty += 1;
        map.set(variantId, line);
        this.pushAddOrder(productId, variantId, isAddons);
      } else {
        // - removes from the most recently added unit for this product.
        variantId = this.popAddOrder(productId, isAddons) || this.findFallbackVariantToRemove(productId, isAddons);
        if (!variantId) return;

        const line = map.get(String(variantId));
        if (!line) return;
        line.qty = Math.max(0, line.qty - 1);
        if (line.qty === 0) {
          map.delete(String(variantId));
        } else {
          map.set(String(variantId), line);
        }
      }

      this.syncProductCardQty(productId, isAddons);
      this.updateUI();
    }

    getActiveStepRoot() {
      return this.step === 'addons' ? this.addonsStep : this.buildStep;
    }

    queryInActiveStep(selector) {
      const root = this.getActiveStepRoot();
      return root ? root.querySelector(selector) : null;
    }

    queryAllInRoot(selector) {
      return Array.from(this.querySelectorAll(selector));
    }

    createBundleGroupId() {
      return `byob-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    }

    buildBundleProperties(bundleGroupId) {
      return {
        _bundle_: bundleGroupId,
      };
    }

    isByobBundleLineItem(item, bundleGroupId) {
      if (!item?.properties || !bundleGroupId) return false;
      return item.properties._bundle_ === bundleGroupId;
    }

    hasByobBundleProperty(item) {
      return Object.prototype.hasOwnProperty.call(item?.properties || {}, '_bundle_');
    }

    isByobGiftLineItem(item) {
      const value = item?.properties?._byob_gift;
      return value === true || value === 'true';
    }

    getEligibleGiftVariantIds(cents) {
      const amount = Math.max(0, Number(cents) || 0);
      const ids = [];
      const seen = new Set();
      this.getNormalizedTiers(this.config.tiers).forEach((tier) => {
        if (amount < tier.thresholdCents || !tier.giftVariantId) return;
        if (seen.has(tier.giftVariantId)) return;
        seen.add(tier.giftVariantId);
        ids.push(tier.giftVariantId);
      });
      return ids;
    }

    buildGiftCartItem(variantId) {
      return {
        id: Number(variantId),
        quantity: 1,
        properties: {
          _byob_gift: 'true',
        },
      };
    }

    appendEligibleGiftItems(items, cents) {
      const list = Array.isArray(items) ? items : [];
      const existing = new Set(list.map((item) => String(item.id)));
      this.getEligibleGiftVariantIds(cents).forEach((variantId) => {
        if (existing.has(String(variantId))) return;
        existing.add(String(variantId));
        list.push(this.buildGiftCartItem(variantId));
      });
      return list;
    }

    async fetchCartState() {
      const getCart = window.eHS?.cart_actions?.getCart;
      return getCart
        ? await getCart(false)
        : await fetch('/cart.js').then((response) => response.json());
    }

    async syncEligibleGiftItems(eligibilityCents) {
      const cart = await this.fetchCartState();
      const eligibleIds = new Set(this.getEligibleGiftVariantIds(eligibilityCents));
      const updates = {};
      const presentIds = new Set();

      (cart?.items || []).forEach((item) => {
        if (!this.isByobGiftLineItem(item)) return;
        const variantId = String(item.variant_id);
        if (eligibleIds.has(variantId)) {
          presentIds.add(variantId);
        } else {
          updates[item.key] = 0;
        }
      });

      const toAdd = [];
      eligibleIds.forEach((variantId) => {
        if (presentIds.has(variantId)) return;
        toAdd.push(this.buildGiftCartItem(variantId));
      });

      if (Object.keys(updates).length) {
        await this.removeCartLineUpdates(updates);
      }
      if (toAdd.length) {
        await this.addItemsToCart(toAdd);
      }
    }

    resolveNextPageUrl(nextUrl) {
      if (!nextUrl) return '';
      const url = new URL(nextUrl, window.location.origin);
      const current = new URL(window.location.href);
      ['view', 'preview_theme_id'].forEach((param) => {
        const value = current.searchParams.get(param);
        if (value) url.searchParams.set(param, value);
      });
      return `${url.pathname}${url.search}`;
    }

    syncPaginationLoader() {
      this.updatePaginationState();
    }

    async removeCartLineUpdates(updates) {
      if (!updates || !Object.keys(updates).length) return;
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      const data = await response.json();
      if (data.status === 422 || data.status === 400) {
        throw new Error(data.description || 'Unable to update cart');
      }
      if (window.eHS?.cart_actions?.getCart) {
        await window.eHS.cart_actions.getCart(true);
      }
      return data;
    }

    async setCartAttributes(attributes) {
      if (!attributes || !Object.keys(attributes).length) return;
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
      });
      const data = await response.json();
      if (data.status === 422 || data.status === 400) {
        throw new Error(data.description || 'Unable to update cart attributes');
      }
      if (window.eHS?.cart_actions?.getCart) {
        await window.eHS.cart_actions.getCart(true);
      }
      return data;
    }

    async removeCommittedBundleItems() {
      if (!this.committedBundleGroupId) return;
      const cart = await this.fetchCartState();
      const updates = {};
      (cart?.items || []).forEach((item) => {
        if (this.isByobBundleLineItem(item, this.committedBundleGroupId) || this.isByobGiftLineItem(item)) {
          updates[item.key] = 0;
        }
      });
      await this.removeCartLineUpdates(updates);
      this.committedBundleGroupId = null;
      this.cartLines = [];
    }

    getCartLineFullPriceCents(line) {
      if (!line || line.isByobGift) return 0;
      const original = Number(line.original_line_price);
      if (Number.isFinite(original) && original > 0) return original;
      const unit = Number(line.original_price ?? line.price) || 0;
      const qty = Number(line.quantity) || 0;
      return unit * qty;
    }

    getPaidCartLinesSubtotalCents({ fullPrice = false } = {}) {
      return this.cartLines.reduce((sum, line) => {
        if (line.isByobGift) return sum;
        if (fullPrice) return sum + this.getCartLineFullPriceCents(line);
        return sum + (Number(line.final_line_price) || 0);
      }, 0);
    }

    getActiveDraft() {
      return this.step === 'addons' ? this.addonsDraft : this.draft;
    }

    getAddonsDraftSubtotalCents() {
      return Array.from(this.addonsDraft.values()).reduce(
        (sum, line) => sum + (Number(line.priceCents) || 0) * (Number(line.qty) || 0),
        0
      );
    }

    getDraftSubtotalCents({ fullPrice = false, includeAddons = true } = {}) {
      if (this.step === 'addons') {
        const cartPart = this.getPaidCartLinesSubtotalCents({ fullPrice });
        const addonsPart = includeAddons ? this.getAddonsDraftSubtotalCents() : 0;
        return cartPart + addonsPart;
      }
      const map = this.draft;
      let total = 0;
      map.forEach((line) => {
        total += line.priceCents * line.qty;
      });
      return total;
    }

    getDraftItemCount() {
      if (this.step === 'addons') {
        const cartCount = this.cartLines.reduce((sum, line) => sum + line.quantity, 0);
        const addonCount = Array.from(this.addonsDraft.values()).reduce((sum, line) => sum + line.qty, 0);
        return cartCount + addonCount;
      }
      const map = this.draft;
      let count = 0;
      map.forEach((line) => {
        count += line.qty;
      });
      return count;
    }

    getNormalizedTiers(sourceTiers) {
      return (Array.isArray(sourceTiers) ? sourceTiers : [])
        .map((tier) => {
          const thresholdDollars = parseFloat(String(tier.threshold ?? '').replace(/[^0-9.]/g, ''));
          const thresholdCents = Number.isFinite(thresholdDollars)
            ? Math.round(thresholdDollars * 100)
            : 0;
          const offer = String(tier.offer || tier.label || '');
          const giftVariantId = tier.giftVariantId != null && tier.giftVariantId !== ''
            ? String(tier.giftVariantId)
            : null;
          return {
            ...tier,
            thresholdCents,
            offer,
            discountPercent: this.parseDiscountPercentFromOffer(offer),
            giftVariantId,
          };
        })
        .filter((tier) => tier.thresholdCents > 0)
        .sort((a, b) => a.thresholdCents - b.thresholdCents);
    }

    parseDiscountPercentFromOffer(offer) {
      const match = String(offer || '').match(/(\d+(?:\.\d+)?)\s*%/);
      if (!match) return null;
      const percent = Number(match[1]);
      return Number.isFinite(percent) ? percent : null;
    }

    getFreeShippingTier(tiers = this.getNormalizedTiers(this.config.tiers)) {
      return tiers.find((tier) => this.isFreeShippingOffer(tier.offer)) || null;
    }

    isFreeShippingOffer(offer) {
      return /free\s*shipping/i.test(String(offer || ''));
    }

    isFreeShippingReached(cents) {
      const shippingTier = this.getFreeShippingTier();
      if (!shippingTier) return false;
      return Math.max(0, Number(cents) || 0) >= shippingTier.thresholdCents;
    }

    updateOtpShippingNotes(cents) {
      const show = this.step === 'addons' && !this.isFreeShippingReached(cents);
      this.querySelectorAll('[data-ctr-byob-otp-shipping-note]').forEach((el) => {
        const inHiddenStep = el.closest(
          '[data-ctr-byob-build-step], [data-ctr-byob-addons-step]'
        )?.hidden;
        if (inHiddenStep) return;
        el.hidden = !show;
      });
    }

    getEligibleDiscountPercent(cents) {
      const amount = Math.max(0, Number(cents) || 0);
      const tiers = this.getNormalizedTiers(this.config.tiers);
      let best = 0;

      tiers.forEach((tier) => {
        if (amount < tier.thresholdCents) return;
        if (tier.discountPercent == null) return;
        if (tier.discountPercent > best) best = tier.discountPercent;
      });

      if (best === 0 && amount >= this.minimumCents && this.firstDiscountPercent > 0) {
        best = this.firstDiscountPercent;
      }

      return best;
    }

    formatUnlockTitle(percent, fallbackTitle) {
      const title = String(fallbackTitle || '').trim();
      if (!percent) return title;
      if (title && /\d+(\.\d+)?\s*%/.test(title)) {
        return title.replace(/\d+(?:\.\d+)?\s*%/, `${percent}%`);
      }
      return `${percent}% off unlocked`;
    }

    updateUnlockBanners(cents) {
      const percent = this.getEligibleDiscountPercent(cents);
      const show = percent > 0;

      this.querySelectorAll('[data-ctr-byob-unlock]').forEach((banner) => {
        const inHiddenStep = banner.closest(
          '[data-ctr-byob-build-step], [data-ctr-byob-addons-step]'
        )?.hidden;
        if (inHiddenStep) return;

        banner.hidden = !show;
        const titleEl = banner.querySelector('[data-ctr-byob-unlock-title]');
        if (titleEl && show) {
          if (!titleEl.dataset.defaultTitle) {
            titleEl.dataset.defaultTitle = titleEl.textContent.trim();
          }
          titleEl.textContent = this.formatUnlockTitle(percent, titleEl.dataset.defaultTitle);
        }
      });
    }

    calcRewardsFillPercent(cents, tiers, modifier = 'condensed') {
      if (!tiers.length) return 0;
      const last = tiers[tiers.length - 1].thresholdCents;
      if (cents <= 0) return 0;

      if (modifier === 'full') {
        const markerPosition = (index) => ((index + 0.5) / tiers.length) * 100;
        // Fill the full track once every reward threshold is met
        if (cents >= last) return 100;

        let fullFill = 0;
        for (let i = 0; i < tiers.length; i += 1) {
          const previousThreshold = i === 0 ? 0 : tiers[i - 1].thresholdCents;
          const currentThreshold = tiers[i].thresholdCents;
          const segmentStart = i === 0 ? 0 : markerPosition(i - 1);
          const segmentEnd = markerPosition(i);

          if (cents >= currentThreshold) {
            fullFill = segmentEnd;
          } else if (cents > previousThreshold) {
            const thresholdRange = currentThreshold - previousThreshold;
            const ratio = thresholdRange > 0
              ? (cents - previousThreshold) / thresholdRange
              : 1;
            fullFill = segmentStart + (segmentEnd - segmentStart) * ratio;
            break;
          } else {
            break;
          }
        }

        return Math.max(0, Math.min(100, fullFill));
      }

      if (cents >= last) return 100;

      let fill = 0;
      for (let i = 0; i < tiers.length; i += 1) {
        const prev = i === 0 ? 0 : tiers[i - 1].thresholdCents;
        const curr = tiers[i].thresholdCents;
        const segmentStart = (i / tiers.length) * 100;
        const segmentEnd = ((i + 1) / tiers.length) * 100;
        if (cents >= curr) {
          fill = segmentEnd;
        } else if (cents > prev) {
          const ratio = (cents - prev) / (curr - prev);
          fill = segmentStart + (segmentEnd - segmentStart) * ratio;
          break;
        } else {
          break;
        }
      }
      return Math.max(0, Math.min(100, fill));
    }

    updateRewardsTimelines(cents) {
      this.querySelectorAll('[data-ctr-byob-rewards]').forEach((root) => {
        const inHiddenStep = root.closest(
          '[data-ctr-byob-build-step], [data-ctr-byob-addons-step]'
        )?.hidden;
        if (inHiddenStep) return;

        const configNode = root.querySelector('[data-ctr-byob-rewards-config]');
        let config = { tiers: this.config.tiers || [], labels: {} };
        if (configNode) {
          try {
            config = { ...config, ...JSON.parse(configNode.textContent) };
          } catch (error) {
            /* keep defaults */
          }
        }

        const tiers = this.getNormalizedTiers(config.tiers);
        const labels = decodeLabelMap(config.labels || {});
        const modifier = root.dataset.modifier || 'full';
        root.style.setProperty('--ctr-byob-tier-count', String(Math.max(tiers.length, 1)));

        root.querySelectorAll('[data-ctr-byob-rewards-current]').forEach((el) => {
          el.textContent = formatMoney(cents);
        });

        const track = root.querySelector('[data-ctr-byob-rewards-track]');
        if (track && tiers.length) {
          const fillPercent = this.calcRewardsFillPercent(cents, tiers, modifier);
          let fill = track.querySelector('.ctr-byob-rewards__fill');
          let markers = Array.from(track.querySelectorAll('.ctr-byob-rewards__marker'));

          if (!fill || markers.length !== tiers.length) {
            track.innerHTML = `
              <div class="ctr-byob-rewards__fill" style="width:${fillPercent}%"></div>
              <div class="ctr-byob-rewards__markers">
                ${tiers
                  .map(
                    () => `
                      <div class="ctr-byob-rewards__marker">
                        <span class="ctr-byob-rewards__dot" aria-hidden="true"></span>
                      </div>
                    `
                  )
                  .join('')}
              </div>
            `;
            fill = track.querySelector('.ctr-byob-rewards__fill');
            markers = Array.from(track.querySelectorAll('.ctr-byob-rewards__marker'));
          } else {
            fill.style.width = `${fillPercent}%`;
          }

          markers.forEach((marker, index) => {
            marker.classList.toggle('is-complete', cents >= tiers[index].thresholdCents);
          });
        }

        const labelsEl = root.querySelector('[data-ctr-byob-rewards-labels]');
        if (labelsEl && modifier === 'full') {
          labelsEl.innerHTML = tiers
            .map((tier, index) => {
              const complete = cents >= tier.thresholdCents;
              const amountLabel =
                tier === tiers[0]
                  ? `${formatMoney(tier.thresholdCents).replace(/\.00$/, '')} min`
                  : formatMoney(tier.thresholdCents).replace(/\.00$/, '');
              return `
                <div
                  class="ctr-byob-rewards__label${complete ? ' is-complete' : ''}"
                  style="--ctr-byob-tier-column:${index + 1}"
                  role="listitem"
                >
                  <span class="ctr-byob-rewards__label-offer">${escapeHtml(tier.offer)}</span>
                  <span class="ctr-byob-rewards__label-amount">${escapeHtml(amountLabel)}</span>
                </div>
              `;
            })
            .join('');
        }

        const nextEl = root.querySelector('[data-ctr-byob-rewards-next]');
        if (nextEl) {
          const nextTier = tiers.find((tier) => cents < tier.thresholdCents);
          if (nextTier) {
            const remaining = nextTier.thresholdCents - cents;
            const template = String(
              nextTier.message_unmet ||
                nextTier.messageUnmet ||
                `${labels.youArePrefix || "You're"} [PRICE] ${labels.awayPrefix || 'away'} ${
                  labels.awayMid || 'from'
                } ${labels.awayUnlocking || 'unlocking'} [OFFER]`
            );
            nextEl.innerHTML = escapeHtml(template)
              .replaceAll('[PRICE]', `<strong>${formatMoney(remaining)}</strong>`)
              .replaceAll('[OFFER]', `<strong>${escapeHtml(nextTier.offer)}</strong>`);
          } else if (tiers.length) {
            nextEl.textContent = labels.allUnlocked || 'All subscription rewards unlocked';
          } else {
            nextEl.textContent = '';
          }
        }
      });
    }

    syncAllProductCardQtys() {
      const isAddons = this.step === 'addons';
      const seen = new Set();
      this.getDraftMap(isAddons).forEach((line) => {
        if (line.productId == null || seen.has(String(line.productId))) return;
        seen.add(String(line.productId));
        this.syncProductCardQty(line.productId, isAddons);
      });

      const scope = isAddons ? this.addonsStep : this.buildStep;
      scope?.querySelectorAll('[data-ctr-byob-card]').forEach((card) => {
        const productId = card.dataset.productId;
        if (!productId || seen.has(String(productId))) return;
        this.syncProductCardQty(productId, isAddons);
      });
    }

    peekLatestVariant(productId, isAddons) {
      if (productId == null) return null;
      const stack = this.getAddOrderMap(isAddons).get(String(productId)) || [];
      for (let i = stack.length - 1; i >= 0; i -= 1) {
        const variantId = String(stack[i]);
        const line = this.getDraftMap(isAddons).get(variantId);
        if (line?.qty > 0) return variantId;
      }
      return this.findFallbackVariantToRemove(productId, isAddons);
    }

    groupDraftLines(map) {
      const groups = [];
      const indexByProduct = new Map();

      map.forEach((line) => {
        const productId = String(line.productId ?? line.variantId);
        let group = indexByProduct.get(productId);
        if (!group) {
          group = {
            productId,
            title: line.title,
            image: line.image,
            totalQty: 0,
            totalCents: 0,
            compareCents: 0,
            optionParts: [],
            lines: [],
          };
          indexByProduct.set(productId, group);
          groups.push(group);
        }

        group.totalQty += line.qty;
        group.totalCents += line.priceCents * line.qty;
        if (line.compareCents && line.compareCents > line.priceCents) {
          group.compareCents += line.compareCents * line.qty;
        }
        if (line.image && !group.image) group.image = line.image;
        if (line.optionSubtitle) {
          group.optionParts.push(
            line.qty > 1 ? `${line.optionSubtitle} × ${line.qty}` : line.optionSubtitle
          );
        }
        group.lines.push(line);
      });

      return groups;
    }

    renderLines(container, map, showPrice) {
      if (!container) return;
      container.innerHTML = '';
      const groups = this.groupDraftLines(map);

      groups.forEach((group) => {
        const li = document.createElement('li');
        li.className = 'ctr-byob-panel__line';
        const optionsHtml = group.optionParts.length
          ? `<p class="ctr-byob-panel__line-options">${escapeHtml(group.optionParts.join(', '))}</p>`
          : '';
        li.innerHTML = `
          <div class="ctr-byob-panel__line-media">
            ${group.image ? `<img src="${group.image}" alt="" width="48" height="48" loading="lazy">` : ''}
          </div>
          <div class="ctr-byob-panel__line-body">
            <p class="ctr-byob-panel__line-title">${escapeHtml(group.title)}</p>
            ${optionsHtml}
            ${
              showPrice
                ? `<p class="ctr-byob-panel__line-price">
                    <span class="ctr-byob-panel__line-price-current">${formatMoney(group.totalCents)}</span>
                    ${
                      group.compareCents > group.totalCents
                        ? `<span class="ctr-byob-panel__line-price-compare">${formatMoney(group.compareCents)}</span>`
                        : ''
                    }
                  </p>`
                : ''
            }
          </div>
          <div class="ctr-byob-panel__line-qty">
            <button type="button" class="ctr-byob-panel__line-qty-btn" data-product-minus data-product-id="${group.productId}" aria-label="${this.qtyAriaLabel(this.config.labels?.decreaseQty, group.title)}">−</button>
            <span class="ctr-byob-panel__line-qty-value">${group.totalQty}</span>
            <button type="button" class="ctr-byob-panel__line-qty-btn" data-product-plus data-product-id="${group.productId}" aria-label="${this.qtyAriaLabel(this.config.labels?.increaseQty, group.title)}">+</button>
          </div>
        `;
        container.appendChild(li);
      });

      container.querySelectorAll('[data-product-minus]').forEach((btn) => {
        btn.addEventListener('click', () => this.adjustProductQtyInDraft(btn.dataset.productId, -1));
      });
      container.querySelectorAll('[data-product-plus]').forEach((btn) => {
        btn.addEventListener('click', () => this.adjustProductQtyInDraft(btn.dataset.productId, 1));
      });
    }

    adjustProductQtyInDraft(productId, delta) {
      if (productId == null) return;
      const isAddons = this.step === 'addons';
      const map = this.getDraftMap(isAddons);

      if (delta > 0) {
        // Cart + repeats the most recently added variant for this product.
        const variantId = this.peekLatestVariant(productId, isAddons);
        if (!variantId) return;
        const line = map.get(String(variantId));
        if (!line) return;
        line.qty += 1;
        map.set(String(variantId), line);
        this.pushAddOrder(productId, variantId, isAddons);
      } else {
        const variantId =
          this.popAddOrder(productId, isAddons) || this.findFallbackVariantToRemove(productId, isAddons);
        if (!variantId) return;
        const line = map.get(String(variantId));
        if (!line) return;
        line.qty = Math.max(0, line.qty - 1);
        if (line.qty === 0) {
          map.delete(String(variantId));
        } else {
          map.set(String(variantId), line);
        }
      }

      this.syncProductCardQty(productId, isAddons);
      this.updateUI();
    }

    updateTierMessages() {
      // Tier copy is shown via the rewards timeline.
      this.querySelectorAll('[data-ctr-byob-tier-messages]').forEach((container) => {
        container.innerHTML = '';
        container.hidden = true;
        container.setAttribute('aria-hidden', 'true');
      });
    }

    getDiscountedSubtotalCents(subtotalCents, eligibilityCents = subtotalCents) {
      const amount = Math.max(0, Number(subtotalCents) || 0);
      const percent = this.getEligibleDiscountPercent(eligibilityCents);
      if (!percent) return amount;
      return Math.round((amount * (100 - percent)) / 100);
    }

    formatItemCount(count) {
      const labels = this.config.labels || {};
      if (count === 1) return labels.itemOne || '1 item';
      const sample = labels.itemOther || '2 items';
      return sample.replace(/\d+/, String(count));
    }

    qtyAriaLabel(template, title) {
      return escapeHtml(String(template || '').replaceAll('__TITLE__', title || ''));
    }

    updateUI() {
      this.ensureFrequencySelection();
      const isBuild = this.step === 'build';
      const draft = isBuild ? this.draft : this.addonsDraft;
      const fullSubtotal = this.getDraftSubtotalCents({ fullPrice: true, includeAddons: false });
      const progressSubtotal = this.getDraftSubtotalCents({ fullPrice: true, includeAddons: true });
      const payableSubtotal = this.getDraftSubtotalCents({ fullPrice: false, includeAddons: true });
      const itemCount = this.getDraftItemCount();
      const buildSubtotal = Array.from(this.draft.values()).reduce(
        (sum, line) => sum + line.priceCents * line.qty,
        0
      );
      const minimumMet = buildSubtotal >= this.minimumCents;
      const atcSubtotal = this.getDiscountedSubtotalCents(buildSubtotal);
      const countLabel = this.formatItemCount(itemCount);

      if (isBuild) {
        this.updateRewardsTimelines(buildSubtotal);
        this.updateUnlockBanners(buildSubtotal);
        this.updateOtpShippingNotes(buildSubtotal);
      } else {
        // Main rewards bar stays subscription-only; grey OTP note uses sub + OTP for free shipping.
        this.updateRewardsTimelines(fullSubtotal);
        this.updateOtpShippingNotes(progressSubtotal);
      }
      this.updateTierMessages();
      this.syncAllProductCardQtys();

      this.querySelectorAll('[data-ctr-byob-item-count], [data-ctr-byob-mobile-item-count]').forEach((el) => {
        if (!this.getActiveStepRoot()?.contains(el)) return;
        el.textContent = countLabel;
      });

      const showPriceInLines = isBuild;
      const activeRoot = this.getActiveStepRoot();
      if (isBuild) {
        this.renderLines(activeRoot?.querySelector('[data-ctr-byob-lines]'), draft, showPriceInLines);
        this.renderLines(activeRoot?.querySelector('[data-ctr-byob-mobile-lines]'), draft, showPriceInLines);
      } else {
        this.renderCartLines(activeRoot?.querySelector('[data-ctr-byob-lines]'));
        this.renderCartLines(activeRoot?.querySelector('[data-ctr-byob-mobile-lines]'));
        this.renderLines(activeRoot?.querySelector('[data-ctr-byob-addon-lines]'), draft, false);
        this.renderLines(activeRoot?.querySelector('[data-ctr-byob-mobile-addon-lines]'), draft, false);
      }

      const totalText = isBuild && buildSubtotal > 0 ? ` — ${formatMoney(atcSubtotal)}` : '';
      const primaryLabel = isBuild
        ? this.enableAddonsStep && this.buildCommitted
          ? this.config.labels?.continue || 'Continue'
          : this.config.labels?.addToCart || 'Add to cart'
        : this.config.labels?.checkout || 'Checkout';

      this.queryAllInRoot('[data-ctr-byob-primary-cta], [data-ctr-byob-mobile-primary-cta]').forEach((btn) => {
        if (!activeRoot?.contains(btn)) return;
        const label = btn.querySelector('[data-ctr-byob-primary-cta-label], [data-ctr-byob-mobile-primary-cta-label]');
        const total = btn.querySelector('[data-ctr-byob-primary-cta-total], [data-ctr-byob-mobile-primary-cta-total]');
        if (label) label.textContent = primaryLabel;
        if (total) total.textContent = isBuild ? totalText : '';
        if (isBuild) {
          btn.disabled =
            this.enableAddonsStep && this.buildCommitted
              ? false
              : !minimumMet || this.draft.size === 0;
        } else {
          btn.disabled = false;
        }
      });

      const mobileTotal = this.queryInActiveStep('[data-ctr-byob-mobile-total]');
      if (mobileTotal) {
        mobileTotal.textContent = formatMoney(isBuild ? buildSubtotal : payableSubtotal);
      }

      const freqTotals = this.getActiveStepRoot()?.querySelectorAll(
        '[data-ctr-byob-frequency-total], [data-ctr-byob-mobile-frequency-total]'
      );
      freqTotals?.forEach((el) => {
        if (!isBuild) {
          el.hidden = false;
          el.textContent = formatMoney(payableSubtotal);
        } else {
          el.hidden = true;
        }
      });
    }

    renderCartLines(container) {
      if (!container) return;
      container.innerHTML = '';
      this.cartLines.forEach((line) => {
        const li = document.createElement('li');
        const isGift = Boolean(line.isByobGift);
        li.className = `ctr-byob-panel__line ctr-byob-panel__line--cart${isGift ? ' ctr-byob-panel__line--gift' : ''}`;
        const image = line.image || line.featured_image?.url || '';
        const priceHtml = isGift
          ? `<p class="ctr-byob-panel__line-price">
              <span class="ctr-byob-panel__line-price-current">${formatMoney(0)}</span>
            </p>`
          : '';
        li.innerHTML = `
          <div class="ctr-byob-panel__line-media">
            ${image ? `<img src="${image}" alt="" width="48" height="48" loading="lazy">` : ''}
          </div>
          <div class="ctr-byob-panel__line-body">
            <p class="ctr-byob-panel__line-title">${escapeHtml(line.product_title || line.title)}</p>
            ${
              formatOptionSubtitle(line)
                ? `<p class="ctr-byob-panel__line-options">${escapeHtml(formatOptionSubtitle(line))}</p>`
                : ''
            }
            ${priceHtml}
          </div>
          <div class="ctr-byob-panel__line-qty">
            <span class="ctr-byob-panel__line-qty-value">${line.quantity}</span>
          </div>
        `;
        container.appendChild(li);
      });
    }

    async refreshCartLines() {
      const cart = await this.fetchCartState();
      const bundleLines = [];
      const giftLines = [];

      (cart?.items || []).forEach((item) => {
        if (item.properties?.['_gwp']) return;

        if (this.isByobGiftLineItem(item)) {
          giftLines.push({ ...item, isByobGift: true, final_line_price: 0 });
          return;
        }

        const isBundleLine = this.committedBundleGroupId
          ? this.isByobBundleLineItem(item, this.committedBundleGroupId)
          : this.hasByobBundleProperty(item);
        if (isBundleLine) {
          bundleLines.push({ ...item, isByobGift: false });
        }
      });

      this.cartLines = [...bundleLines, ...giftLines];
    }

    bindPanel() {
      this.queryAllInRoot('[data-ctr-byob-primary-cta], [data-ctr-byob-mobile-primary-cta]').forEach((btn) => {
        if (btn.dataset.bound === 'true') return;
        btn.dataset.bound = 'true';
        btn.addEventListener('click', () => {
          if (this.step === 'build') {
            if (this.enableAddonsStep && this.buildCommitted) {
              this.goToAddonsStep(false);
            } else {
              this.batchAddBuildItems();
            }
          } else {
            this.checkoutAddons();
          }
        });
      });

      this.queryAllInRoot('[data-ctr-byob-back-cta], [data-ctr-byob-mobile-back-cta]').forEach((btn) => {
        if (btn.dataset.bound === 'true') return;
        btn.dataset.bound = 'true';
        btn.addEventListener('click', () => this.goToBuildStep());
      });

      this.queryAllInRoot('[data-ctr-byob-mobile-toggle]').forEach((toggle) => {
        if (toggle.dataset.bound === 'true') return;
        toggle.dataset.bound = 'true';
        const drawerId = toggle.getAttribute('aria-controls');
        const drawer = drawerId ? document.getElementById(drawerId) : null;
        toggle.addEventListener('click', () => {
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          const nextExpanded = !expanded;
          toggle.setAttribute('aria-expanded', String(nextExpanded));
          this.classList.toggle('ctr-byob--mobile-expanded', nextExpanded);

          if (!drawer) return;

          if (nextExpanded) {
            drawer.hidden = false;
            drawer.setAttribute('aria-hidden', 'false');
            requestAnimationFrame(() => {
              drawer.classList.add('is-open');
            });
            return;
          }

          drawer.classList.remove('is-open');
          drawer.setAttribute('aria-hidden', 'true');

          const onTransitionEnd = (event) => {
            if (event.target !== drawer) return;
            drawer.removeEventListener('transitionend', onTransitionEnd);
            if (toggle.getAttribute('aria-expanded') !== 'true') {
              drawer.hidden = true;
            }
          };
          drawer.addEventListener('transitionend', onTransitionEnd);
        });
      });
    }

    async addItemsToCart(items, { openCart = false } = {}) {
      const addFn = window.eHS?.cart_actions?.addMultipleToCart;
      if (addFn) {
        return addFn(items, true, openCart);
      }

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await response.json();
      if (data.status === 422 || data.status === 400) {
        alert(data.description);
        throw new Error(data.description);
      }
      // Open immediately on add success — don't wait for a follow-up getCart.
      if (openCart) {
        this.openSideCart();
      }
      if (window.eHS?.cart_actions?.getCart) {
        await window.eHS.cart_actions.getCart(true);
      }
      return data;
    }

    openSideCart() {
      if (typeof window.setState === 'function') {
        window.setState('cartOpen', true);
        return;
      }

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    }

    resetBuildDraft() {
      this.draft.clear();
      this.draftAddOrder.clear();
      this.buildCommitted = false;
      this.committedBundleGroupId = null;
      this.cartLines = [];

      this.grid?.querySelectorAll('[data-ctr-byob-card]').forEach((card) => {
        const valueEl = card.querySelector('[data-ctr-byob-qty-value]');
        if (valueEl) valueEl.textContent = '0';
        card.classList.remove('ctr-byob-card--added');
        const stepper = card.querySelector('[data-ctr-byob-card-stepper]');
        if (stepper && card.dataset.cardLayout === 'simplified') {
          stepper.hidden = true;
        }
      });

      this.ensureFrequencySelection();
      this.updateUI();
    }

    async batchAddBuildItems() {
      if (this.draft.size === 0) return;
      if (this.isAddingToCart) return;
      const bundleGroupId = this.createBundleGroupId();
      const items = [];
      let buildSubtotal = 0;

      this.draft.forEach((line) => {
        const planId = this.resolveSellingPlanId(line.product, line.variantId);
        if (!planId) return;
        buildSubtotal += (Number(line.priceCents) || 0) * (Number(line.qty) || 0);
        items.push({
          id: line.variantId,
          quantity: line.qty,
          selling_plan: planId,
          properties: this.buildBundleProperties(bundleGroupId),
        });
      });

      if (!items.length) {
        alert(this.config.labels?.subscriptionAddFailed || 'Unable to add subscription items. Check selling plans.');
        return;
      }

      this.appendEligibleGiftItems(items, buildSubtotal);

      const addFn = window.eHS?.cart_actions?.addMultipleToCart;
      if (!addFn) {
        console.warn('ctr-byob: cart_actions.addMultipleToCart unavailable, using fetch fallback');
      }

      this.isAddingToCart = true;
      this.setPrimaryCtaBusy(true);

      try {
        if (!this.enableAddonsStep) {
          // Open sidecart as soon as /cart/add.js succeeds (same as site ATC).
          // Bundle attribute is non-blocking so it doesn't delay the drawer.
          await this.addItemsToCart(items, { openCart: true });
          this.setCartAttributes({ _bundle_cart: 'true' }).catch((error) => {
            console.error('ctr-byob: failed to set cart attributes', error);
          });
          this.resetBuildDraft();
          return;
        }

        await this.addItemsToCart(items, { openCart: false });
        this.committedBundleGroupId = bundleGroupId;
        this.buildCommitted = true;
        await this.goToAddonsStep(true);
      } catch (error) {
        console.error('ctr-byob: batch add failed', error);
      } finally {
        this.isAddingToCart = false;
        this.setPrimaryCtaBusy(false);
      }
    }

    setPrimaryCtaBusy(isBusy) {
      this.queryAllInRoot('[data-ctr-byob-primary-cta], [data-ctr-byob-mobile-primary-cta]').forEach((btn) => {
        if (isBusy) {
          btn.dataset.wasDisabled = btn.disabled ? 'true' : 'false';
          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');
        } else {
          btn.removeAttribute('aria-busy');
          if (btn.dataset.wasDisabled === 'true') {
            btn.disabled = true;
          } else {
            btn.disabled = false;
          }
          delete btn.dataset.wasDisabled;
        }
      });
      if (!isBusy) this.updateUI();
    }

    async checkoutAddons() {
      const items = [];
      this.addonsDraft.forEach((line) => {
        if (!line.qty) return;
        items.push({
          id: line.variantId,
          quantity: line.qty,
        });
      });

      // Gift thresholds ignore one-time add-ons; only subscription bundle lines count.
      const eligibilityCents = this.getPaidCartLinesSubtotalCents({ fullPrice: true });

      if (items.length) {
        try {
          await this.addItemsToCart(items);
        } catch (error) {
          console.error('ctr-byob: add-ons batch add failed', error);
          return;
        }
      }

      try {
        await this.syncEligibleGiftItems(eligibilityCents);
      } catch (error) {
        console.error('ctr-byob: gift sync failed', error);
        return;
      }

      try {
        await this.setCartAttributes({ _bundle_cart: 'true' });
      } catch (error) {
        console.error('ctr-byob: failed to set cart attributes', error);
        return;
      }

      window.location.href = '/checkout';
    }

    async goToAddonsStep(refreshCart) {
      if (!this.enableAddonsStep || !this.addonsStep) return;
      if (refreshCart !== false) {
        await this.refreshCartLines();
      }
      this.step = 'addons';
      this.dataset.step = 'addons';
      this.buildStep.hidden = true;
      this.addonsStep.hidden = false;
      if (this.tabsWrap) this.tabsWrap.hidden = true;

      this.querySelectorAll('[data-ctr-byob-frequency-total], [data-ctr-byob-mobile-frequency-total]').forEach((el) => {
        el.hidden = false;
      });

      this.addonsDraft.clear();
      this.addonsAddOrder.clear();
      this.addonsGrid?.querySelectorAll('[data-ctr-byob-card]').forEach((card) => {
        const valueEl = card.querySelector('[data-ctr-byob-qty-value]');
        if (valueEl) valueEl.textContent = '0';
        card.classList.remove('ctr-byob-card--added');
        const stepper = card.querySelector('[data-ctr-byob-card-stepper]');
        if (stepper && card.dataset.cardLayout === 'simplified') {
          stepper.hidden = true;
        }
      });
      this.ensureFrequencySelection();
      this.updateUI();
      this.refreshYotpoWidgets();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    async goToBuildStep() {
      try {
        if (this.buildCommitted) {
          await this.removeCommittedBundleItems();
        }
      } catch (error) {
        console.error('ctr-byob: failed to remove committed bundle items', error);
      }

      this.step = 'build';
      this.dataset.step = 'build';
      this.buildCommitted = false;
      this.buildStep.hidden = false;
      if (this.addonsStep) this.addonsStep.hidden = true;
      if (this.tabsWrap) this.tabsWrap.hidden = false;

      this.updateUI();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    bindInfiniteScroll() {
      if (!this.grid || !this.loader) return;
      if (this.paginationObserver) this.paginationObserver.disconnect();

      this.paginationObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && this.shouldTriggerIntersectionLoad()) {
              this.loadMoreProducts();
            }
          });
        },
        { root: null, rootMargin: '0px 0px 320px 0px', threshold: 0 }
      );

      if (!this.scrollPaginationBound) {
        this.scrollPaginationBound = true;
        const onScroll = () => {
          this.armPaginationFromScroll();
          if (!this.shouldTriggerIntersectionLoad()) return;
          this.loadMoreProducts();
        };
        window.addEventListener('scroll', onScroll, { passive: true });
      }

      this.updatePaginationState();
    }

    async loadMoreProducts() {
      const nextUrl = this.resolveNextPageUrl(this.grid?.dataset.nextUrl);
      if (!nextUrl || this.loadingMore) return;

      this.loadingMore = true;
      if (this.loader) {
        this.loader.hidden = false;
        this.loader.classList.add('is-loading');
      }
      if (this.paginationObserver) {
        this.paginationObserver.disconnect();
      }

      try {
        let requestUrl = nextUrl;
        const visitedUrls = new Set();

        while (requestUrl && !visitedUrls.has(requestUrl)) {
          visitedUrls.add(requestUrl);
          const response = await fetch(requestUrl);
          if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const nextGrid =
            doc.querySelector('[data-ctr-byob-build-step] [data-ctr-byob-grid]') ||
            doc.querySelector('[data-ctr-byob-grid]');

          if (!nextGrid) {
            this.grid.dataset.nextUrl = '';
            break;
          }

          const newCards = Array.from(nextGrid.querySelectorAll('[data-ctr-byob-card]'));
          const resolvedNextUrl = this.resolveNextPageUrl(nextGrid.dataset.nextUrl || '');
          const hasAdvancingUrl = Boolean(
            resolvedNextUrl &&
              resolvedNextUrl !== requestUrl &&
              !visitedUrls.has(resolvedNextUrl)
          );
          this.grid.dataset.nextUrl = hasAdvancingUrl ? resolvedNextUrl : '';

          if (newCards.length) {
            this.appendLoadedCards(newCards);
            this.bindCards(this.grid);
            this.collectFrequencyOptions();
            this.populateFrequencySelects();
            this.refreshYotpoWidgets();
            break;
          }

          if (!hasAdvancingUrl) break;
          requestUrl = resolvedNextUrl;
        }

        this.applyTabFilter();
      } catch (error) {
        console.error('ctr-byob: failed to load more products', error);
      } finally {
        this.loadingMore = false;
        if (this.loader) {
          this.loader.classList.remove('is-loading');
        }

        const visibleCount = this.getVisibleCardCount();
        // Prevent cascade loads while still near the bottom after a successful page fetch.
        // Re-arm only after the user scrolls away from the grid bottom.
        if (this.activeFilter === 'all' || visibleCount > 0) {
          this.paginationArmed = false;
        }

        this.updatePaginationState();
        this.maybeAutoLoadForActiveTab(visibleCount);
      }
    }

    collapseMobilePanels() {
      this.classList.remove('ctr-byob--mobile-expanded');
      this.querySelectorAll('[data-ctr-byob-mobile-toggle]').forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
      });
      this.querySelectorAll('[data-ctr-byob-mobile-drawer]').forEach((drawer) => {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        drawer.hidden = true;
      });
    }

    syncMobilePanelVisibility(stickyTop) {
      const isMobile = window.matchMedia('(max-width: 1023px)').matches;
      const isBuildStep = this.step === 'build' && this.buildStep && !this.buildStep.hidden;
      const tabsAvailable = this.tabsWrap && !this.tabsWrap.hidden;
      const tabsTop = tabsAvailable ? this.tabsWrap.getBoundingClientRect().top : Infinity;
      const shouldShow = Boolean(
        isMobile && isBuildStep && tabsAvailable && tabsTop <= stickyTop + 1
      );

      this.classList.toggle('ctr-byob--mobile-panel-visible', shouldShow);
      if (!shouldShow) this.collapseMobilePanels();
    }

    syncStickyTabsOffset() {
      const offset = getStickyTopOffset();
      this.style.setProperty('--ctr-byob-sticky-top', `${offset}px`);
      this.syncMobilePanelVisibility(offset);

      const previewBar = document.getElementById('PBarNextFrameWrapper');
      const previewHeight = previewBar ? Math.round(previewBar.getBoundingClientRect().height) : 0;
      this.querySelectorAll('.ctr-byob-panel__mobile').forEach((panel) => {
        panel.style.bottom = previewHeight > 0 ? `${previewHeight}px` : '';
      });
    }
  }

  if (!customElements.get('ctr-byob')) {
    customElements.define('ctr-byob', CtrByob);
  }

  function initByob(scope) {
    scope.querySelectorAll('ctr-byob').forEach((el) => {
      if (typeof el.init === 'function' && !el.bound) {
        el.connectedCallback();
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => initByob(document));
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target?.querySelector?.('ctr-byob')) {
      initByob(event.target);
    }
  });
})();



(function () {
  if (window.__ctrHomepageAliaAutoOpenInitialized) return;
  window.__ctrHomepageAliaAutoOpenInitialized = true;

  var ALIA_CAMPAIGN_ID = 39355;
  var AUTO_OPEN_DELAY_MS = 8000;
  var AUTO_OPEN_STORAGE_KEY = "ctrAliaHomepageAutoOpen";
  var RETURN_OFFER_INTERACTION_SELECTOR =
    "[data-ctr-return-offer-announcement-link], [data-ctr-return-offer-link], [data-ctr-return-offer-announcement]";

  var autoOpenTimer = null;
  var userInteracted = false;

  function normalizePath(path) {
    return (String(path || "/").replace(/\/+$/, "") || "/").toLowerCase();
  }

  function isHomepage() {
    var rootPath =
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
        ? window.Shopify.routes.root
        : "/";

    return normalizePath(window.location.pathname) === normalizePath(rootPath);
  }

  function hasAutoOpenAlreadyFired() {
    try {
      return sessionStorage.getItem(AUTO_OPEN_STORAGE_KEY) === "1";
    } catch (_error) {
      return false;
    }
  }

  function markAutoOpenFired() {
    try {
      sessionStorage.setItem(AUTO_OPEN_STORAGE_KEY, "1");
    } catch (_error) {}
  }

  function cancelAutoOpen() {
    userInteracted = true;

    if (autoOpenTimer) {
      window.clearTimeout(autoOpenTimer);
      autoOpenTimer = null;
    }
  }

  function openAliaPopup() {
    window.alia = window.alia || [];
    window.alia.push({
      type: "open",
      campaignID: ALIA_CAMPAIGN_ID
    });
  }

  function scheduleAutoOpen() {
    if (!isHomepage()) return;
    if (hasAutoOpenAlreadyFired()) return;
    if (userInteracted) return;

    autoOpenTimer = window.setTimeout(function () {
      if (userInteracted) return;
      if (hasAutoOpenAlreadyFired()) return;

      markAutoOpenFired();
      openAliaPopup();
    }, AUTO_OPEN_DELAY_MS);
  }

  function onInteraction(event) {
    var target = event.target;
    var el = target && target.nodeType === 1 ? target : target && target.parentElement;

    if (!el || !el.closest) return;
    if (!el.closest(RETURN_OFFER_INTERACTION_SELECTOR)) return;

    cancelAutoOpen();
  }

  if (!isHomepage()) return;

  document.addEventListener("click", onInteraction, true);
  scheduleAutoOpen();
})();

(function forceHomepageReturnOffer() {
  var OFFER_KEY = "everyday_dose";

  function normalizePath(path) {
    return (String(path || "/").replace(/\/+$/, "") || "/").toLowerCase();
  }

  function isHomepage() {
    var rootPath =
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
        ? window.Shopify.routes.root
        : "/";

    return normalizePath(window.location.pathname) === normalizePath(rootPath);
  }

  function parseConfig(root) {
    try {
      return JSON.parse(root.getAttribute("data-config") || "{}");
    } catch (_error) {
      return {};
    }
  }

  function applyQueryParams(url, rawParams) {
    var raw = String(rawParams || "").trim();
    if (!raw) return;

    var segments = raw.indexOf("\n") !== -1
      ? raw.split("\n")
      : raw.split("&");

    segments.forEach(function (segment) {
      var separatorIndex = segment.indexOf("=");
      if (separatorIndex === -1) return;

      var key = segment.slice(0, separatorIndex).trim();
      var value = segment.slice(separatorIndex + 1).trim();

      if (key) {
        url.searchParams.set(key, value);
      }
    });
  }

  function buildHref(offer) {
    if (!offer.destinationUrl) return "#";

    try {
      var url = new URL(offer.destinationUrl, window.location.origin);

      applyQueryParams(url, offer.queryParams);

      return (
        url.pathname +
        url.search +
        String(offer.buyboxAnchor || "")
      );
    } catch (_error) {
      return offer.destinationUrl;
    }
  }

  function findOffer(config) {
    var offers = config.offers || [];
    var selectedOffer = null;

    offers.some(function (offer) {
      if (
        offer.offerKey === OFFER_KEY &&
        offer.destinationUrl
      ) {
        selectedOffer = offer;
        return true;
      }

      return false;
    });

    // Falls back to the first configured destination if the key is absent.
    if (!selectedOffer) {
      offers.some(function (offer) {
        if (offer.destinationUrl) {
          selectedOffer = offer;
          return true;
        }

        return false;
      });
    }

    return selectedOffer;
  }

  function showFloatingButton(root, offer, href) {
    var buttons = root.querySelectorAll(
      "[data-ctr-return-offer-link]"
    );

    buttons.forEach(function (button) {
      var mobileLabel = button.querySelector(
        ".ctr-offcanvas-return-offer__label--mobile"
      );
      var desktopLabel = button.querySelector(
        ".ctr-offcanvas-return-offer__label--desktop"
      );

      button.href = href;
      button.hidden = false;
      button.removeAttribute("hidden");

      if (mobileLabel && offer.buttonLabelMobile) {
        mobileLabel.textContent = offer.buttonLabelMobile;
      }

      if (desktopLabel && offer.buttonLabelDesktop) {
        desktopLabel.textContent = offer.buttonLabelDesktop;
      }
    });

    root.classList.add("is-visible");
  }

  function showAnnouncementBars(offer, href) {
    var bars = document.querySelectorAll(
      "[data-ctr-return-offer-announcement]"
    );

    bars.forEach(function (bar) {
      bar.classList.add("announcement-bar--return-offer");

      if (offer.announcementBgColor) {
        bar.style.setProperty(
          "--announcement-bar-background",
          offer.announcementBgColor
        );
        bar.style.background = offer.announcementBgColor;
      }

      if (offer.announcementTextColor) {
        bar.style.color = offer.announcementTextColor;
      }

      bar
        .querySelectorAll(".announcement-bar-marquee__entry")
        .forEach(function (entry) {
          var link = entry.querySelector("a");

          if (!link) {
            var span = entry.querySelector("span");

            if (span) {
              link = document.createElement("a");
              span.replaceWith(link);
            }
          }

          if (!link) return;

          link.href = href;
          link.textContent = offer.announcementText || "";
          link.setAttribute(
            "data-ctr-return-offer-announcement-link",
            ""
          );
        });
    });

    return bars.length > 0;
  }

  function applyReturnOffer() {
    if (!isHomepage()) return true;

    var root = document.querySelector("[data-ctr-return-offer]");
    if (!root) return false;

    var config = parseConfig(root);
    var offer = findOffer(config);

    if (!offer) return false;

    var href = buildHref(offer);

    showFloatingButton(root, offer, href);

    return showAnnouncementBars(offer, href);
  }

  if (applyReturnOffer()) return;

  // Handles Intelligems running before the theme elements are rendered.
  var observer = new MutationObserver(function () {
    if (applyReturnOffer()) {
      observer.disconnect();
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  window.setTimeout(function () {
    observer.disconnect();
  }, 15000);
})();
